import type { SnapshotListStatus } from 'services/snapshotsService';

export type TimestampLike =
  | { _seconds: number; _nanoseconds?: number }
  | { seconds: number }
  | string
  | number
  | null
  | undefined
  | unknown;

export function snapshotTimestampToDate(value: TimestampLike): Date | null {
  if (value == null) return null;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (typeof record._seconds === 'number') {
      return new Date(record._seconds * 1000);
    }
    if (typeof record.seconds === 'number') {
      return new Date(record.seconds * 1000);
    }
  }
  return null;
}

export function formatSnapshotDay(value: TimestampLike): string {
  const date = snapshotTimestampToDate(value);
  if (!date) return '-';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export function formatSnapshotLongDate(value: TimestampLike): string {
  const date = snapshotTimestampToDate(value);
  if (!date) return '-';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Both lists are in and overlap is available (or expiring). */
export function canOpenSnapshotDetail(status: SnapshotListStatus): boolean {
  return status === 'ready' || status === 'expiring_soon';
}

export function statusLabel(
  status: SnapshotListStatus,
  partnerCompanyName?: string,
): string {
  switch (status) {
    case 'ready':
      return 'Ready';
    case 'waiting_on_partner':
      return `Waiting on ${partnerCompanyName || 'partner'}`;
    case 'waiting_on_you':
      return 'Waiting on you';
    case 'expiring_soon':
      return 'Expiring soon';
    case 'processing':
      return 'Processing';
    case 'expired':
      return 'Expired';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
}

export function statusColors(status: SnapshotListStatus): {
  bg: string;
  color: string;
} {
  switch (status) {
    case 'ready':
      return { bg: 'green.100', color: 'green.600' };
    case 'waiting_on_partner':
    case 'waiting_on_you':
      return { bg: 'purple.100', color: 'purple.600' };
    case 'expiring_soon':
      return { bg: 'yellow.100', color: 'yellow.700' };
    case 'processing':
      return { bg: 'yellow.100', color: 'yellow.700' };
    default:
      return { bg: 'gray.100', color: 'gray.500' };
  }
}

export type SnapshotLifecycleStage = 'lead' | 'opportunity' | 'customer';

/**
 * Same lifecycle matrix as homepage HubSpot strategic lists.
 * `youStage` is the viewer's account; `themStage` is the other company.
 */
export function classifyOverlapPlaybooks(
  youStage: SnapshotLifecycleStage | string,
  themStage: SnapshotLifecycleStage | string,
): Array<'upsells' | 'accelerate' | 'generate' | 'partnerships' | 'leadToLead'> {
  const keys: Array<
    'upsells' | 'accelerate' | 'generate' | 'partnerships' | 'leadToLead'
  > = [];
  const themIsCustOrOpp = themStage === 'customer' || themStage === 'opportunity';
  const themIsOppOrLead = themStage === 'opportunity' || themStage === 'lead';

  if (youStage === 'customer' && themIsCustOrOpp) keys.push('upsells');
  if (youStage === 'opportunity' && themIsCustOrOpp) keys.push('accelerate');
  if (youStage === 'lead' && themIsCustOrOpp) keys.push('generate');
  if (youStage === 'customer' && themIsOppOrLead) keys.push('partnerships');
  if (youStage === 'lead' && themStage === 'lead') keys.push('leadToLead');

  return keys;
}

/** Initiator rate is stored as `overlapRate`; partner rate is the other side when sizes differ. */
export function snapshotOverlapRates(counts: {
  overlapRate: number;
  overlapRateMin: number;
  overlapRateMax: number;
}) {
  const initiatorRate = counts.overlapRate;
  const partnerRate =
    counts.overlapRateMin === counts.overlapRateMax
      ? counts.overlapRate
      : counts.overlapRate === counts.overlapRateMin
        ? counts.overlapRateMax
        : counts.overlapRateMin;
  return { initiatorRate, partnerRate };
}

/** Overlap % for the company viewing the snapshot (initiator vs partner). */
export function viewerSnapshotOverlapRate(
  counts: {
    overlapRate: number;
    overlapRateMin: number;
    overlapRateMax: number;
  },
  isInitiator: boolean,
) {
  const { initiatorRate, partnerRate } = snapshotOverlapRates(counts);
  return isInitiator ? initiatorRate : partnerRate;
}
